import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Payment from "containers/OrganisationProfile/payment/index";
import RazorpayPayment from "containers/OrganisationProfile/RazorPay/index";
import { signupContext } from "context/SignupContext";
import { signInContext } from "context/SigninContext";
import "containers/OrganisationProfile/organisationProfile.scss";

// A thin wrapper to reuse the signup payment + Razorpay flow for upgrades.
// It hydrates signupContext with the selected plan, user/org objects, and then
// renders Payment -> RazorpayPayment.
// Uses the same OrganisationProfile container structure for proper theme support.
function UpgradePaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    selectedPlanData,
    setSelectedPlanData,
    setIsTypeSelect,
    setUser: setSignupUser,
    setOrgnization: setSignupOrg
  } = useContext(signupContext);
  const { user, orgnization, isTypeSelect } = useContext(signInContext);

  const [step, setStep] = useState(1); // 1 = summary/payment, 2 = Razorpay
  const [success, setSuccess] = useState(false);

  const planFromNav = location.state?.plan || null;

  // Get plan from navigation state only (no sessionStorage)
  const effectivePlan = useMemo(() => {
    if (planFromNav) return { billingPeriod: "monthly", ...planFromNav };
    return null;
  }, [planFromNav]);

  useEffect(() => {
    // Hydrate signup context with user/org and selected plan so the shared
    // payment components can run.
    setIsTypeSelect(isTypeSelect || "individual");
    if (user) setSignupUser(user);
    if (orgnization) setSignupOrg(orgnization);
    if (effectivePlan) {
      setSelectedPlanData(effectivePlan);
    }
  }, [
    effectivePlan,
    isTypeSelect,
    orgnization,
    setIsTypeSelect,
    setSelectedPlanData,
    setSignupOrg,
    setSignupUser,
    user
  ]);

  // Navigate to homepage when payment is successful.
  // Pass the plan we just upgraded to (effectivePlan.planId) so Homepage can show Ysentinel immediately.
  useEffect(() => {
    if (success) {
      console.log("✅ Payment successful, navigating to homepage...");
      const upgradedPlanId = effectivePlan?.planId != null ? Number(effectivePlan.planId) : undefined;
      window.location.reload();
      const timer = setTimeout(() => {
        navigate("/homepage", {
          replace: true,
          state: { fromUpgrade: true, upgradedPlanId }
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [success, navigate, effectivePlan]);

  // If we somehow land here without plan info, return to upgrade selection.
  if (!effectivePlan) {
    navigate("/upgrade-plan", { replace: true });
    return null;
  }

  // Show success state instead of payment form when payment is successful
  if (success) {
    return (
      <section className="Organisation">
        <div className="container OrganisationContainer">
          <div style={{ 
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center", 
            justifyContent: "center", 
            minHeight: "400px",
            textAlign: "center",
            color: "var(--text-grey-strong)"
          }}>
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>✅</div>
            <h2 style={{ marginBottom: "8px", color: "var(--text-grey-strong)" }}>Payment Successful!</h2>
            <p style={{ color: "var(--text-grey-strong)", opacity: 0.8 }}>Your plan has been upgraded successfully.</p>
            <p style={{ color: "var(--text-grey-strong)", opacity: 0.6, marginTop: "8px", fontSize: "14px" }}>Redirecting to homepage...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="Organisation">
      <div className="container OrganisationContainer">
        {step === 1 && (
          <Payment
            change="payment"
            setChange={() => {}}
            selectPrice={effectivePlan}
            setSuccess={setSuccess}
            success={success}
            nextStep={() => setStep(2)}
          />
        )}
        {step === 2 && (
          <RazorpayPayment
            nextStep={() => {}}
            successState={success}
            setSuccessState={setSuccess}
            setStep={() => setStep(1)}
            setChange={() => {}}
            setIndividualSuccessState={() => {}}
            onDismissFunction={() => setStep(1)}
          />
        )}
      </div>
    </section>
  );
}

export default UpgradePaymentPage;
